import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import SkillsProps from '../../model/skills-props';

const skills = [
  { name: 'Flutter', color: '#41D0FD', bg: 'rgba(65, 208, 253, 0.15)' },
  { name: 'JavaScript', color: '#F5DE19', bg: 'rgba(245, 222, 25, 0.15)' },
  { name: 'TypeScript', color: '#3178C6', bg: 'rgba(49, 120, 198, 0.15)' },
  { name: 'React', color: '#61DAFB', bg: 'rgba(97, 218, 251, 0.15)' },
  { name: 'Next.js', color: '#FFFFFF', bg: 'rgba(255, 255, 255, 0.1)' },
  { name: 'Node.js', color: '#0FA958', bg: 'rgba(15, 169, 88, 0.15)' },
  { name: 'Python', color: '#3776AB', bg: 'rgba(55, 118, 171, 0.15)' },
  { name: 'NestJS', color: '#E0234E', bg: 'rgba(224, 35, 78, 0.15)' },
  { name: 'MongoDB', color: '#47A248', bg: 'rgba(71, 162, 72, 0.15)' },
  { name: 'Docker', color: '#2496ED', bg: 'rgba(36, 150, 237, 0.15)' },
  { name: 'Firebase', color: '#FFCA28', bg: 'rgba(255, 202, 40, 0.15)' },
  { name: 'Figma', color: '#F24E1E', bg: 'rgba(242, 78, 30, 0.15)' },
  { name: 'CSS3', color: '#1572B6', bg: 'rgba(21, 114, 182, 0.15)' },
  { name: 'NGINX', color: '#009639', bg: 'rgba(0, 150, 57, 0.15)' },
  { name: 'GraphQL', color: '#E10098', bg: 'rgba(225, 0, 152, 0.15)' },
  { name: 'Redis', color: '#DC382D', bg: 'rgba(220, 56, 45, 0.15)' },
  { name: 'PostgreSQL', color: '#4169E1', bg: 'rgba(65, 105, 225, 0.15)' },
  { name: 'AWS', color: '#FF9900', bg: 'rgba(255, 153, 0, 0.15)' },
  { name: 'Git', color: '#F05032', bg: 'rgba(240, 80, 50, 0.15)' },
  { name: 'Linux', color: '#FCC624', bg: 'rgba(252, 198, 36, 0.15)' },
];

// Duplicate for seamless loop
const row1Skills = [...skills.slice(0, 10), ...skills.slice(0, 10)];
const row2Skills = [...skills.slice(10), ...skills.slice(10)];

interface SkillTagProps {
  name: string;
  color: string;
  bg: string;
}

function SkillTag({ name, color, bg }: SkillTagProps) {
  return (
    <motion.div
      whileHover={{ scale: 1.1, y: -5 }}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        background: bg,
        border: `1px solid ${color}30`,
        borderRadius: '50px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        marginRight: '16px',
      }}
    >
      <Box
        sx={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: color,
          boxShadow: `0 0 10px ${color}`,
        }}
      />
      <Typography
        sx={{
          fontFamily: 'Fira Code, monospace',
          fontSize: '0.9rem',
          fontWeight: 500,
          color: color,
          letterSpacing: '0.02em',
        }}
      >
        {name}
      </Typography>
    </motion.div>
  );
}

interface MarqueeRowProps {
  skills: SkillTagProps[];
  direction: 'left' | 'right';
  duration: number;
}

function MarqueeRow({ skills, direction, duration }: MarqueeRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '200px',
          zIndex: 2,
          pointerEvents: 'none',
        },
        '&::before': {
          left: 0,
          background: 'linear-gradient(90deg, #0A0A0F 0%, transparent 100%)',
        },
        '&::after': {
          right: 0,
          background: 'linear-gradient(-90deg, #0A0A0F 0%, transparent 100%)',
        },
      }}
    >
      <motion.div
        animate={{
          x: direction === 'left' ? ['0%', '-50%'] : ['-50%', '0%'],
        }}
        transition={{
          x: {
            repeat: Infinity,
            repeatType: 'loop',
            duration: duration,
            ease: 'linear',
          },
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {skills.map((skill, index) => (
          <SkillTag key={`${skill.name}-${index}`} {...skill} />
        ))}
      </motion.div>
    </Box>
  );
}

export default function Skills({ isMobile }: SkillsProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <Box
      component="section"
      id="skills"
      ref={ref}
      sx={{
        py: { xs: 10, md: 15 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <Typography
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.85rem',
              color: '#44D482',
              letterSpacing: '0.1em',
              mb: 2,
            }}
          >
            {'// TECH STACK'}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: { xs: '2.5rem', md: '4rem' },
              color: '#fff',
              mb: 2,
            }}
          >
            MY SKILLS
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '1rem',
              maxWidth: '500px',
              mb: 6,
            }}
          >
            Technologies I work with to bring ideas to life
          </Typography>
        </motion.div>
      </Container>

      {/* Infinite Marquee */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <MarqueeRow skills={row1Skills} direction="left" duration={30} />
          <MarqueeRow skills={row2Skills} direction="right" duration={25} />
        </Box>
      </motion.div>

      {/* Terminal-style info */}
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Box
            sx={{
              mt: 8,
              p: 4,
              background: 'rgba(26, 26, 36, 0.6)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              border: '1px solid rgba(68, 212, 130, 0.15)',
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.85rem',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#FF5F56' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#FFBD2E' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#27CA40' }} />
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                ~/skills
              </Typography>
            </Box>
            
            <Box sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              <Box sx={{ mb: 1 }}>
                <span style={{ color: '#44D482' }}>$</span>{' '}
                <span style={{ color: '#fff' }}>cat</span> skills.json
              </Box>
              <Box sx={{ pl: 2, color: 'rgba(255,255,255,0.5)' }}>
                {'{'}
              </Box>
              <Box sx={{ pl: 4 }}>
                <span style={{ color: '#44D482' }}>"status"</span>:{' '}
                <span style={{ color: '#F5DE19' }}>"always_learning"</span>,
              </Box>
              <Box sx={{ pl: 4 }}>
                <span style={{ color: '#44D482' }}>"passion"</span>:{' '}
                <span style={{ color: '#F5DE19' }}>"building_cool_stuff"</span>,
              </Box>
              <Box sx={{ pl: 4 }}>
                <span style={{ color: '#44D482' }}>"coffee_level"</span>:{' '}
                <span style={{ color: '#61DAFB' }}>Infinity</span>
              </Box>
              <Box sx={{ pl: 2, color: 'rgba(255,255,255,0.5)' }}>
                {'}'}
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}
